import React, { useState, useReducer, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useReadOnly } from 'hooks/read-only';

import { Stack } from 'ui/stack';
import { Loader } from 'ui/loader';
import { Typography } from 'ui/typography';
import { SearchField } from 'ui/search-field';

import TableComponent from 'components/partials/table-component/table-component';
import Button from 'components/partials/button/button';
import EmptySpaceContent from 'components/partials/empty-space-content/empty-space-content';
import FilterStatusForm from 'containers/admin/clients/client/forms/filter-status-form/filter-status-form';

import { usersReducer } from 'containers/admin/reducer';
import { clientsTableSettings } from 'containers/admin/clients/clients-table-settings';
import {
  SEARCH_CHANGE,
  FILTER_CHANGE,
  PAGE_CHANGE,
} from 'containers/admin/actions';

import { useClientsList } from 'hooks/api/use-clients';

import Icons from 'assets/icons';
import ResponsiveWrapper from 'components/shared/others/responsive-wrapper/responsive-wrapper';

const TODAY = new Date();
const stringifiedToday = TODAY.toISOString().split('T')[0];

TODAY.setDate(TODAY.getDate() + 90);
const stringifiedExpireSoonDate = TODAY.toISOString().split('T')[0];

const initialState = {
  search: '',
  pageIndex: 0,
};

interface ReducerState {
  search: string;
  pageIndex: number;
}

interface ReducerAction {
  type: string;
  payload?: unknown;
}

const ClientsAdminPage = (): React.JSX.Element => {
  const [state, dispatch] = useReducer(
    usersReducer as unknown as (
      state: ReducerState,
      action: ReducerAction
    ) => ReducerState,
    initialState
  );
  const [filterStatus, setFilterStatus] = useState('all');
  const [pageSize, setPageSize] = useState(100);

  const { search, pageIndex } = state;

  const readOnly = useReadOnly();

  const [sortingParams, setSortingParams] = useState([
    { id: 'name', desc: false },
  ]);

  const buildFilterParams = () => {
    let expireDate: Record<string, string> | undefined;

    switch (filterStatus) {
      case 'active':
        expireDate = { greaterThanOrEqual: stringifiedToday };
        break;
      case 'expired':
        expireDate = { lessThan: stringifiedToday };
        break;
      case 'expireSoon':
        expireDate = {
          greaterThanOrEqual: stringifiedToday,
          lessThan: stringifiedExpireSoonDate,
        };
        break;
    }

    return {
      search: { value: search },
      expireDate,
    };
  };

  const {
    data,
    pagination,
    loading: isLoading,
    isFetching,
  } = useClientsList({
    pageIndex,
    pageSize,
    sortBy: sortingParams,
    filters: buildFilterParams(),
  });

  const totalRecords = pagination?.totalRecords ?? 0;

  const handleSearch = useCallback(
    (payload: string) => dispatch({ type: SEARCH_CHANGE, payload }),
    []
  );

  const handleFilter = useCallback((value: string | undefined) => {
    dispatch({ type: FILTER_CHANGE });
    setFilterStatus(value ?? 'all');
  }, []);

  const handlePagination = useCallback(
    (payload: number) => dispatch({ type: PAGE_CHANGE, payload }),
    []
  );

  const filterForm = (
    <FilterStatusForm status={filterStatus} onClick={handleFilter} />
  );

  const renderContent = () => {
    if (isLoading) {
      return <Loader loading fullScreen={false} />;
    }
    if (data.length > 0 || isFetching) {
      return (
        <TableComponent
          columns={clientsTableSettings}
          data={data}
          pageIndex={pageIndex}
          pageSize={pageSize}
          setPagination={handlePagination}
          setAmount={(updater) => {
            const next = updater({ pageSize, totalRecords });

            setPageSize(next.pageSize);
          }}
          sortingParams={sortingParams}
          setSortingParams={setSortingParams}
          totalRecords={totalRecords}
          isLoading={isFetching}
          toolbarStart={filterForm}
        />
      );
    }

    return (
      <>
        {filterForm}
        <EmptySpaceContent
          customClass="mt-4 flex-1"
          image={<Icons.EmptyImageAll customClass="size-20" />}
          text={
            filterStatus === 'all' || search
              ? 'Sorry, no results found. Please try a different keyword.'
              : 'Sorry, there are no results with such filter parameters.'
          }
        />
      </>
    );
  };

  return (
    <ResponsiveWrapper customClass="flex flex-col">
      <Stack
        direction="row"
        sx={{ alignItems: 'center', justifyContent: 'space-between', my: 2.5 }}
      >
        <Typography data-testid="admin-clients-heading" variant="h1">
          Clients
        </Typography>
        {!readOnly && (
          <RouterLink to="/admin/clients/new" data-testid="add-client-button">
            <Button
              variant="brand-filled"
              size="lg"
              iconBefore={<Icons.Plus aria-label="PlusIcon" />}
            >
              Add a client
            </Button>
          </RouterLink>
        )}
      </Stack>

      <SearchField onSearch={handleSearch} sx={{ mb: 2 }} />

      {renderContent()}
    </ResponsiveWrapper>
  );
};

export default ClientsAdminPage;
